/**
 * Email-safe HTML for cart line previews (Admin Cart Reports nudges, etc.).
 * Typography and greys align with transactional templates (order confirmation) + gold accent #B99040.
 */

import { getProductImageUrls } from '../services/productGalleryService';
import {
    EMAIL_BODY_FONT_STACK,
    EMAIL_TEXT_PRIMARY,
    EMAIL_TEXT_MUTED,
    EMAIL_BORDER_LIGHT,
    EMAIL_ACCENT_GOLD,
    EMAIL_PRE_BG,
} from './emailBrandStyles';

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Same field fallbacks as AdminDashboard getCartItemImageUrl, plus ImageURLs[0]. */
export function getCartItemImageUrlFromItem(item) {
    const fromSlots = getProductImageUrls(item)[0] || '';
    const raw =
        fromSlots ||
        item?.imageUrl ||
        item?.imageURL ||
        item?.image ||
        item?.ImageURL ||
        item?.['Image URL'] ||
        item?.['image_url'] ||
        '';
    const t = String(raw || '').trim();
    return t || null;
}

/**
 * Cart lines with qty > 0, capped (default first 5 for nudge emails).
 */
export function getCartLineItemsForPreview(cart, maxItems = 5) {
    const items = Array.isArray(cart?.cartItems) ? cart.cartItems : [];
    return items
        .filter((i) => (i?.name || i?.sku || i?.code) && Number(i?.qty ?? i?.quantity ?? 0) > 0)
        .slice(0, maxItems);
}

/**
 * Plain-text lines for the first N cart lines (email text part).
 */
export function buildCartTopItemsPlainText(cart, maxItems = 5) {
    const rows = getCartLineItemsForPreview(cart, maxItems);
    return rows
        .map((i) => {
            const qty = Number(i.qty ?? i.quantity ?? 0);
            const sku = i.sku || i.code || '';
            const name = i.name || sku || 'Item';
            const label = name + (sku && name !== sku ? ` (${sku})` : '');
            return `${qty}× ${label}`.trim();
        })
        .join('\n');
}

/**
 * Table-based horizontal “product cards” for email HTML (first N lines).
 * Returns empty string if there are no lines.
 */
export function buildCartTopItemsHtml(cart, maxItems = 5) {
    const rows = getCartLineItemsForPreview(cart, maxItems);
    if (rows.length === 0) return '';

    const cards = rows.map((item) => {
        const qty = Number(item.qty ?? item.quantity ?? 0);
        const sku = item.sku || item.code || '';
        const name = item.name || sku || 'Item';
        const imgUrl = getCartItemImageUrlFromItem(item);
        const safeName = escapeHtml(name);
        const safeSku = sku ? escapeHtml(sku) : '';
        const skuBlock = safeSku
            ? `<div style="font-family:${EMAIL_BODY_FONT_STACK};font-size:12px;color:${EMAIL_TEXT_MUTED};margin:0 0 8px 0;">${safeSku}</div>`
            : '';
        const qtyBlock = `<div style="font-family:${EMAIL_BODY_FONT_STACK};font-size:13px;font-weight:600;color:${EMAIL_ACCENT_GOLD};margin:0;">Qty: ${qty}</div>`;

        const safeImgAttr = imgUrl ? escapeHtml(imgUrl) : '';
        const imgTd = imgUrl
            ? `<td width="120" valign="middle" style="width:120px;min-width:120px;padding:0;background:${EMAIL_PRE_BG};vertical-align:middle;"><img src="${safeImgAttr}" alt="${safeName}" width="120" height="120" style="display:block;width:120px;height:120px;object-fit:cover;border:0;margin:0;line-height:0;" /></td>`
            : `<td width="120" style="width:120px;min-width:120px;padding:0;background:${EMAIL_PRE_BG};">&nbsp;</td>`;

        const textTd =
            `<td valign="top" style="padding:12px 14px;font-family:${EMAIL_BODY_FONT_STACK};vertical-align:top;">` +
            `<div style="display:block;width:100%;margin:0 0 8px 0;padding:0;font-family:${EMAIL_BODY_FONT_STACK};font-size:15px;font-weight:600;color:${EMAIL_TEXT_PRIMARY};line-height:1.35;word-wrap:break-word;">${safeName}</div>` +
            skuBlock +
            qtyBlock +
            '</td>';

        return (
            `<div style="margin:12px 0;max-width:100%;">` +
            `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:560px;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:1px solid ${EMAIL_BORDER_LIGHT};border-radius:8px;background:#ffffff;">` +
            `<tr>${imgTd}${textTd}</tr>` +
            `</table></div>`
        );
    }).join('');

    return `<div style="margin:8px 0 16px 0;">${cards}</div>`;
}
